import { Injectable, NgZone } from '@angular/core';

import { IGroup, IPagedResponse, IPagingOptions, LanguageDirection, SortDirection } from './interfaces';
import { ITableSettings } from '../table/interfaces';
import { TranslationMapKey } from '../enums';
import { TranslateService } from '@ngx-translate/core';
// import { DateConversion } from '../enums';

@Injectable({
    providedIn: 'root'
})
export class SharedService {
    private readonly rightToLeftLanguages = new Set(['he-IL', 'ar-AE']);

    constructor(
        private readonly ngZone: NgZone,
        private readonly translateSvc: TranslateService,
    ) { }

    getLanguageDirection(language: string): LanguageDirection {
        const isLeftToRightLanguage = !this.rightToLeftLanguages.has(language);
        return isLeftToRightLanguage ? LanguageDirection.ltr : LanguageDirection.rtl;
    }

    executeInNgZone(fn: () => void): void {
        this.ngZone.run(() => fn());
    }

    executeOnNextLoop(fn: () => void): void {
        window.setTimeout(() => fn());
    }

    clone<T>(obj: T): T {
        return JSON.parse(JSON.stringify(obj));
    }

    createDefaultTableSettings(sortFieldName: string, sortDirection: SortDirection): ITableSettings {
        const tableSettings: ITableSettings = {
            page: {
                limit: 5,
                offset: 0
            },
            sort: {
                fieldName: sortFieldName,
                sortDirection: sortDirection
            },
            // dateConversion: DateConversion.objectTime
        };
        return tableSettings;
    }

    getGroups<T>(items: T[], keySelector: (item: T) => string): IGroup<T>[] {
        const grpMap: { [key: string]: any[] } = {};
        for (const item of items) {
            const key = keySelector(item);
            if (grpMap[key]) {
                grpMap[key].push(item);
            } else {
                grpMap[key] = [item];
            }
        }

        const result: IGroup<T>[] = Object.keys(grpMap).map(x => ({ key: x, items: grpMap[x] } as IGroup<T>));
        return result;
    }

    sortByString<T>(array: T[], valueSelector: (item: T) => string): void {
        if (!array) {
            return;
        }
        if (array.length <= 1) {
            return;
        }
        array.sort((left, right) =>
            (valueSelector(left) || '').toLowerCase().localeCompare((valueSelector(right) || '').toLowerCase())
        );
    }

    getDistinctArray<T>(source: T[], keySelector?: (item: T) => any): T[] {
        if (!keySelector) {
            return Array.from(new Set(source));
        }
        const distinctArr: T[] = [];
        const hashObj: any = {};
        for (const item of source) {
            const key = keySelector ? keySelector(item) : item;
            if (!hashObj[key]) {
                hashObj[key] = true;
                distinctArr.push(item);
            }
        }
        return distinctArr;
    }

    createPagingOptionsFromPagedResponse(pagedResponse: IPagedResponse): IPagingOptions {
        const result: IPagingOptions = {
            limit: pagedResponse.limit, offset: pagedResponse.offset,
            object_count: pagedResponse.object_count, pageSizes: this.getPageSizes()
        };
        return result;
    }

    createDefaultPagingOptions(): IPagingOptions {
        const result: IPagingOptions = {
            limit: 10, offset: 0, object_count: 0, pageSizes: this.getPageSizes()
        };
        return result;
    }

    getPageSizes(): number[] {
        return [5, 10, 20, 50];
    }

    translate(key: TranslationMapKey): string {
        return this.translateSvc.instant(key);
    }
}
